import { isEqual, omit, orderBy } from 'lodash';
import querystring from 'querystring';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useVirtual } from 'react-virtual';
import { useGetStoreInfoQuery } from 'store/services/segmentsApi';
import type { JourneyProductList } from 'store/services/types';
import { useParseURLQueryParams } from 'utils';
import { ProductImage } from '../ProductImage';
import { JourneyContext } from './JourneyContext';

interface FirstOrderContainerProps {
  data: JourneyProductList[] | undefined;
}

export const FirstOrderContainer = ({ data }: FirstOrderContainerProps) => {
  const history = useHistory();
  const { query } = useParseURLQueryParams();
  const [search, setSearch] = useState('');

  const { firstProduct, setFirstProduct } = useContext(JourneyContext);

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  useEffect(() => {
    if (query.first_product_id) {
      const selectedProduct = data?.find(
        (item) => String(item.id) === query.first_product_id
      );
      setFirstProduct(selectedProduct);
    }
  }, [data, query.first_product_id, setFirstProduct]);

  const sortedData = orderBy(data, (item) => item.unique_customers, 'desc');

  const filteredData = search
    ? sortedData.filter((item) =>
        item.title.toLowerCase().includes(search.toLowerCase())
      )
    : sortedData;

  const parentRef = useRef<any>();
  const rowVirtualizer = useVirtual({
    size: filteredData?.length,
    parentRef,
    estimateSize: useCallback(() => 142, []),
  });

  const handleSelectProduct = (index: number) => {
    const selectedProduct = filteredData[index];

    if (isEqual(selectedProduct, firstProduct)) {
      setFirstProduct(undefined);
      history.push({
        search: querystring.stringify(
          omit(
            {
              ...query,
            },
            ['first_product_id', 'second_product_id', 'third_product_id']
          )
        ),
      });
      return;
    }

    setFirstProduct(selectedProduct);
    history.push({
      search: querystring.stringify(
        omit(
          {
            ...query,
            first_product_id: selectedProduct?.id,
          },
          ['second_product_id', 'third_product_id']
        )
      ),
    });
  };

  return (
    <div className="relative h-full pl-5 pr-1.5">
      <div className="pt-5 pr-3.5">
        <input
          type="text"
          className="w-full border-segments-gray rounded-segments-10px "
          placeholder="search product"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {!!!filteredData?.length && data?.length && (
        <div className="absolute inset-0 grid h-full place-items-center">
          <p>No product found</p>
        </div>
      )}

      <div
        ref={parentRef}
        className="pr-2 mt-2 List scrollbar-thin scrollbar-thumb-segments-darker-gray-75 scrollbar-thumb-rounded-md scrollable-parent"
        style={{
          height: `730px`,
          width: `100%`,
          overflow: 'auto',
        }}
      >
        <div
          className="truncate divide-y ListInner"
          style={{
            height: `${rowVirtualizer.totalSize}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.virtualItems.map((virtualRow) => (
            <div
              key={virtualRow.index}
              className="py-2 group virtualized-child"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <button
                className={`relative w-full h-full flex items-start space-x-4 p-4  rounded-segments-4px group-hover:bg-segments-gray-F8 group-hover:opacity-100  ${
                  firstProduct?.id === filteredData[virtualRow.index].id
                    ? 'bg-segments-gray-F8  border-2 border-segments-accent-color-1 opacity-100'
                    : 'opacity-50 '
                } `}
                onClick={() => handleSelectProduct(virtualRow.index)}
              >
                <ProductImage
                  productId={filteredData[virtualRow.index].id}
                  className="object-contain w-16 h-16 bg-white rounded-lg"
                  height="64"
                  width="64"
                />
                <div className="space-y-2 text-left">
                  <p
                    className={`font-bold leading-4 whitespace-pre-line text-sm line-clamp-3 ${
                      firstProduct?.id === filteredData[virtualRow.index].id &&
                      'text-segments-accent-color-1'
                    }`}
                  >
                    {filteredData[virtualRow.index].title}
                  </p>

                  <p className="text-black text-opacity-50 text-segments-13px">
                    Customers:{' '}
                    {filteredData[
                      virtualRow.index
                    ].unique_customers.toLocaleString()}{' '}
                  </p>
                  <p className="text-black text-opacity-50 text-segments-13px">
                    Avg. CLV:{' '}
                    {filteredData[virtualRow.index].aclv?.toLocaleString(
                      'en-US',
                      {
                        style: 'currency',
                        currency: storeInfo?.currency || 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </p>
                </div>
                <div className="absolute inset-0 items-center justify-end hidden p-4 text-right group-hover:flex">
                  <svg
                    width="28"
                    height="28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="14" cy="14" r="14" fill="#125970" />
                    <path
                      d="M12 20l5.625-5.625L12 8.75"
                      stroke="#fff"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
